/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */
//standard libraries
import React from "react";
import { useLocation, useParams } from "react-router-dom";
import { Canceler } from "axios";
import { useTranslation } from "react-i18next";

//custom libraries
import { useCustomerValue, useProgressValue } from "../context";
import { useSelectedStoreValue } from "../context";
import { useOrgResource } from "./";
import { useCustomer } from "./use-customer";
import { useCustomerHandler } from "./rest";

export const useCustomerDetails = () => {
  const location: any = useLocation();
  const { customerId } = useParams<{ customerId: string }>();
  const { t } = useTranslation();
  const {
    toggleCustomerStatus,
    clickResetPassword,
    fetchCustomer,
    cusinfo,
    setCusinfo,
    handleChange,
    updateCustomer,
    canSave,
    editFormData,
    setEditFormData,
    initializeEditForm,
    displayAlert,
    setDisplayAlert,
    fetchCustomerV2,
    openMsg,
    setOpenMsg,
  } = useCustomer();

  const { searchCustomerV2Request, getCustomerByLogonIdRequest } =
    useCustomerHandler();

  const { setInProgress } = useProgressValue();

  const { setSearchResult, searchResult } = useCustomerValue();
  const { selectedStore } = useSelectedStoreValue();
  const { orgInfo, setOrgInfo, getOrgById } = useOrgResource();
  const cancels: any = { cancels: [] };
  const isV9 = process.env.COMMERCE_VERSION === "9";

  const fetchCustomerAndUpdateSearchResult = (id: string) => {
    return fetchCustomer(id).then((customer: any) => {
      if (searchResult?.items?.length > 0) {
        const items = searchResult.items.map((c: any) => {
          if (c.id === customer.id) {
            setCusinfo(customer);
            return customer;
          } else {
            return c;
          }
        });
        setSearchResult(Object.assign({ ...searchResult }, { items }));
      }
    });
  };

  const fetchCustomerAndUpdateSearchResultV2 = (logonId: string) => {
    const [res, canceler] = searchCustomerV2Request({ logonId });
    cancels.cancels = canceler;
    let data: { userDataBeans: any[] } = { userDataBeans: [] };
    res
      .then((r: any) => {
        data = r.data;
        const cusinfoPromises = data.userDataBeans.map((userDataBean: any) => {
          const [cusinfoRes, cusinfoCanceler] = getCustomerByLogonIdRequest(
            userDataBean.userRegistry.logonId
          );
          cancels.cancels = [...cancels.cancels, ...cusinfoCanceler];
          return cusinfoRes;
        });
        return Promise.all(cusinfoPromises);
      })
      .then((responses: any) => {
        for (let i = 0; i < data.userDataBeans.length; i++) {
          data.userDataBeans[i].customerDetails =
            responses[i].data.resultList[0];
        }
        if (searchResult.userDataBeans.length > 0) {
          const items = searchResult.userDataBeans.map((c: any) => {
            if (c.logonId === logonId) {
              setCusinfo(data.userDataBeans[0]);
              return data.userDataBeans[0];
            } else {
              return c;
            }
          });
          searchResult.userDataBeans = items;
          setSearchResult({ ...searchResult });
        }
      })
      .catch((e) => console.log(e));
  };

  const changeCustomerStatus = (customer: any) => {
    setInProgress(true);
    toggleCustomerStatus(customer)
      .then((r: any) => {
        isV9
          ? fetchCustomerAndUpdateSearchResult(customer.id)
          : fetchCustomerAndUpdateSearchResultV2(customer.logonId);
      })
      .catch((e: any) => console.log(e))
      .finally(() => {
        setInProgress(false);
      });
  };

  const clickSubmit = (event: any) => {
    event.preventDefault();
    updateCustomer()
      .then((r: any) => {
        setDisplayAlert(true);
        setTimeout(() => setDisplayAlert(false), 4000);
        return (
          editFormData?.id &&
          fetchCustomerAndUpdateSearchResult(editFormData?.id)
        );
      })
      .catch((e: any) => console.log(e));
  };

  React.useEffect(() => {
    if (location.state && location.state.customer) {
      setCusinfo(location.state.customer);
      selectedStore.type === "BMH" &&
        getOrgById(location.state.customer.parentOrganizationId).then((r) => {
          setOrgInfo(r.address);
        });
    } else if (customerId) {
      fetchCustomer(customerId)
        .then((r: any) => {
          selectedStore.type === "BMH" &&
            getOrgById(r.parentOrganizationId).then((r) => {
              setOrgInfo(r.address);
            });
        })
        .catch((e: any) => console.log(e));
    }
  }, [location, customerId]);

  React.useEffect(() => {
    return () => {
      cancels.cancels.forEach((cancel: Canceler) => {
        return cancel();
      });
    };
  }, []);

  return {
    selectedStore,
    changeCustomerStatus,
    clickResetPassword,
    fetchCustomer,
    cusinfo,
    setCusinfo,
    t,
    orgInfo,
    handleChange,
    clickSubmit,
    canSave,
    editFormData,
    initializeEditForm,
    displayAlert,
    setDisplayAlert,
    openMsg,
    setOpenMsg,
  };
};
